import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { parseArabicNumber, formatNumber } from '@/utils/numberUtils';

const ForexConverterModal = ({ isOpen, onClose }) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [direction, setDirection] = useState('A'); // A: Foreign->SYP, B: SYP->Foreign
  const [usdRate, setUsdRate] = useState('12000');
  const [eurRate, setEurRate] = useState('14000');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Load from localStorage on mount
  useEffect(() => {
    const savedUsdRate = localStorage.getItem('rate_usd_old_syp');
    const savedEurRate = localStorage.getItem('rate_eur_old_syp');
    const savedCurrency = localStorage.getItem('last_forex_currency');
    const savedDirection = localStorage.getItem('last_forex_direction');

    if (savedUsdRate) setUsdRate(savedUsdRate);
    if (savedEurRate) setEurRate(savedEurRate);
    if (savedCurrency) setCurrency(savedCurrency);
    if (savedDirection) setDirection(savedDirection);
  }, []);

  // Save to localStorage on changes
  useEffect(() => {
    localStorage.setItem('rate_usd_old_syp', usdRate);
  }, [usdRate]);

  useEffect(() => {
    localStorage.setItem('rate_eur_old_syp', eurRate);
  }, [eurRate]);

  useEffect(() => {
    localStorage.setItem('last_forex_currency', currency);
  }, [currency]);

  useEffect(() => {
    localStorage.setItem('last_forex_direction', direction);
  }, [direction]);

  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleConvert = () => {
    setError('');
    setResult(null);

    const amountValue = parseArabicNumber(amount);
    const rateValue = parseArabicNumber(currency === 'USD' ? usdRate : eurRate);

    if (amountValue <= 0) {
      setError('الرجاء إدخال المبلغ');
      return;
    }

    if (rateValue <= 0) {
      setError('الرجاء إدخال سعر الصرف');
      return;
    }

    let resultValue;
    let resultCurrency;

    if (direction === 'A') {
      // Foreign -> SYP (Old)
      resultValue = Math.round(amountValue * rateValue);
      resultCurrency = 'ل.س قديم';
    } else {
      // SYP (Old) -> Foreign
      resultValue = (amountValue / rateValue).toFixed(2);
      resultCurrency = currency;
    }

    setResult({ value: resultValue, currency: resultCurrency });
  };

  const handleReset = () => {
    setAmount('');
    setResult(null);
    setError('');
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const currentRate = currency === 'USD' ? usdRate : eurRate;
  const setCurrentRate = currency === 'USD' ? setUsdRate : setEurRate;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content forex-modal" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="forex-title">
        <div className="modal-header">
          <h2 id="forex-title" className="modal-title">محول العملات</h2>
          <button 
            ref={closeButtonRef}
            className="modal-close" 
            onClick={onClose}
            aria-label="إغلاق"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="modal-body">
          {/* Currency Selection */}
          <div className="input-group">
            <label className="label">العملة</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="currency"
                  value="USD"
                  checked={currency === 'USD'}
                  onChange={(e) => setCurrency(e.target.value)}
                />
                <span>دولار أمريكي (USD)</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="currency"
                  value="EUR"
                  checked={currency === 'EUR'}
                  onChange={(e) => setCurrency(e.target.value)}
                />
                <span>يورو (EUR)</span>
              </label>
            </div>
          </div>

          {/* Direction Selection */}
          <div className="input-group">
            <label className="label">اتجاه التحويل</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="direction"
                  value="A"
                  checked={direction === 'A'}
                  onChange={(e) => setDirection(e.target.value)}
                />
                <span>{currency} ← ل.س قديم</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="direction"
                  value="B"
                  checked={direction === 'B'}
                  onChange={(e) => setDirection(e.target.value)}
                />
                <span>ل.س قديم ← {currency}</span>
              </label>
            </div>
          </div>

          {/* Exchange Rate Input */}
          <div className="input-group">
            <label htmlFor="rate" className="label">
              سعر الصرف (1 {currency} = ... ل.س قديم)
            </label>
            <input
              type="text"
              id="rate"
              className="input"
              value={currentRate}
              onChange={(e) => {
                setError('');
                setCurrentRate(e.target.value);
              }}
              inputMode="numeric"
              pattern="[0-9]*"
            />
          </div>

          {/* Amount Input */}
          <div className="input-group">
            <label htmlFor="amount" className="label">
              {direction === 'A' ? `المبلغ (${currency})` : 'المبلغ (ل.س قديم)'}
            </label>
            <input
              type="text"
              id="amount"
              className="input"
              placeholder="أدخل المبلغ"
              value={amount}
              onChange={(e) => {
                setError('');
                setAmount(e.target.value);
              }}
              inputMode="numeric"
              pattern="[0-9]*"
            />
          </div>

          {/* Action Buttons */}
          <div className="button-group">
            <button className="btn-primary" onClick={handleConvert}>
              حوّل
            </button>
            <button className="btn-outline" onClick={handleReset}>
              مسح
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="result-section">
              <h3 className="result-title">النتيجة</h3>
              <div className="forex-result">
                <span className="forex-result-value num" dir="ltr">
                  {typeof result.value === 'number' 
                    ? formatNumber(result.value, result.currency === 'ل.س قديم' ? 0 : 2)
                    : result.value
                  }
                </span>
                <span className="forex-result-currency">{result.currency}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForexConverterModal;
